import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Bot, BriefcaseBusiness, ChevronRight, CloudOff, Home, X } from "lucide-react";

const ONBOARDING_KEY = "shields:mobile-onboarding-v1";
const steps = [
  { title: "Book or find work without the clutter.", body: "Shields keeps the important next step clear, whether you are arranging a job or running one.", icon: Home },
  { title: "Manage jobs from one calm workspace.", body: "Messages, schedules, payments and progress stay together.", icon: BriefcaseBusiness },
  { title: "Ask Shields AI whenever you need help.", body: "Shields AI can explain what to do next. You choose whether to take action.", icon: Bot },
] as const;

function shouldOfferOnboarding(pathname: string) {
  if (typeof window === "undefined" || !/^\/(customer|partner|owner)(\/|$)/.test(pathname)) return false;
  try {
    return window.matchMedia("(max-width: 767px)").matches && window.localStorage.getItem(ONBOARDING_KEY) !== "done";
  } catch {
    return false;
  }
}

export function AppExperienceLayer() {
  const pathname = useRouterState({ select: state => state.location.pathname });
  const [online, setOnline] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setOnline(navigator.onLine);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    if (!shouldOfferOnboarding(pathname)) return;
    const timer = window.setTimeout(() => setShowOnboarding(true), 550);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  function finishOnboarding() {
    try { window.localStorage.setItem(ONBOARDING_KEY, "done"); } catch { /* Storage can be disabled. */ }
    setShowOnboarding(false);
  }

  const current = steps[step] ?? steps[0];
  const CurrentIcon = current.icon;

  return <>
    {!online && <div role="status" aria-live="polite" className="fixed inset-x-3 top-[calc(.75rem+env(safe-area-inset-top))] z-[120] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm text-[#071a3d] shadow-xl">
      <CloudOff className="h-5 w-5 shrink-0 text-amber-700" aria-hidden />
      <span><strong className="block">You are offline</strong><span className="block text-xs text-[#506782]">Check your connection and retry any action that did not finish.</span></span>
    </div>}
    <DialogPrimitive.Root open={showOnboarding} onOpenChange={open => { if (!open) finishOnboarding(); }}>
      <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-[129] bg-[#071a38]/45 backdrop-blur-sm" />
      <DialogPrimitive.Content className="fixed bottom-3 left-1/2 z-[130] max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-md -translate-x-1/2 overflow-y-auto rounded-[28px] border border-white/80 bg-white p-0 shadow-xl focus:outline-none sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2">
        <div className="relative bg-[#071a38] px-6 pb-7 pt-6 text-white">
          <button type="button" onClick={finishOnboarding} className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 focus-visible:outline focus-visible:outline-2" aria-label="Skip introduction"><X className="h-5 w-5" aria-hidden /></button>
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#e5c27f]/40 bg-white/10 text-[#efd59f]"><CurrentIcon className="h-6 w-6" aria-hidden /></span>
          <p className="mt-5 text-xs font-bold tracking-wider text-[#efd59f]">WELCOME TO SHIELDS</p>
          <DialogPrimitive.Title className="mt-2 max-w-sm text-2xl font-extrabold leading-tight">{current.title}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-3 max-w-sm text-sm leading-6 text-white/85">{current.body}</DialogPrimitive.Description>
        </div>
        <div className="px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-5">
          <p className="mb-4 text-xs font-semibold text-[#506782]">Step {step + 1} of {steps.length}</p>
          <button type="button" onClick={() => step === steps.length - 1 ? finishOnboarding() : setStep(value => value + 1)} className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#153e80] px-5 text-sm font-extrabold text-white focus-visible:outline focus-visible:outline-2">
            {step === steps.length - 1 ? "Start using Shields" : "Continue"}<ChevronRight className="h-4 w-4" aria-hidden />
          </button>
          {step < steps.length - 1 && <button type="button" onClick={finishOnboarding} className="mt-2 min-h-11 w-full text-sm font-semibold text-[#506782]">Skip for now</button>}
        </div>
      </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  </>;
}
